import { useRef } from "react";

// Lightweight, dependency-free Python syntax highlighting. The preset functions are a tiny
// restricted subset (def/return/if, numbers, math.*, safe builtins), so a regex tokenizer
// is plenty — no Prism/CodeMirror dependency to install into the Docker frontend.

const KEYWORDS = new Set([
  "def", "return", "if", "elif", "else", "and", "or", "not", "in", "is",
  "None", "True", "False", "while", "for", "pass", "lambda", "yield",
  "import", "from", "as", "with", "try", "except", "finally", "raise",
  "global", "nonlocal", "del", "assert", "break", "continue", "class",
]);
const BUILTINS = new Set([
  "min", "max", "abs", "round", "int", "float", "pow", "math",
  "len", "sum", "range", "str", "bool", "list", "dict", "set", "tuple",
  "sorted", "enumerate", "zip", "any", "all", "isinstance", "getattr",
  "print", "type", "repr", "map", "filter", "self",
]);

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Order matters: comments, strings (f/r/b prefixes and triple quotes included), numbers,
// then identifiers; gaps pass through escaped.
const TOKEN =
  /(#[^\n]*)|([fFrRbBuU]{0,2}(?:"""[\s\S]*?"""|'''[\s\S]*?'''|"(?:\\.|[^"\\\n])*"|'(?:\\.|[^'\\\n])*'))|(\b\d[\d_]*\.?\d*(?:[eE][+-]?\d+)?\b)|([A-Za-z_]\w*)/g;

export function highlightPython(code: string): string {
  let out = "";
  let last = 0;
  let prevWord = "";
  let m: RegExpExecArray | null;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(code)) !== null) {
    out += escapeHtml(code.slice(last, m.index));
    const [full, comment, str, num, ident] = m;
    if (comment) {
      out += `<span class="tok-comment">${escapeHtml(comment)}</span>`;
    } else if (str) {
      out += `<span class="tok-string">${escapeHtml(str)}</span>`;
    } else if (num) {
      out += `<span class="tok-number">${escapeHtml(num)}</span>`;
    } else if (ident) {
      let cls = "";
      const isCall = /^\s*\(/.test(code.slice(m.index + full.length));
      if (KEYWORDS.has(ident)) cls = "tok-keyword";
      else if (BUILTINS.has(ident)) cls = "tok-builtin";
      else if (prevWord === "def") cls = "tok-func";
      else if (isCall) cls = "tok-call";
      out += cls
        ? `<span class="${cls}">${escapeHtml(ident)}</span>`
        : escapeHtml(ident);
      prevWord = ident;
    }
    last = m.index + full.length;
  }
  out += escapeHtml(code.slice(last));
  return out;
}

// Shared text metrics so the highlighted <pre> and the editable <textarea> line up exactly.
const TEXT_STYLE: React.CSSProperties = {
  margin: 0,
  border: 0,
  padding: "8px 10px",
  fontFamily:
    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
  fontSize: "12px",
  lineHeight: "1.5",
  tabSize: 4,
  whiteSpace: "pre",
  overflowWrap: "normal",
};

// Token colours for both light and dark (scoped to .python-code so nothing leaks).
const TOKEN_STYLES = `
  .python-code .tok-keyword { color: #a626a4; font-weight: 600; }
  .python-code .tok-builtin { color: #4078f2; }
  .python-code .tok-func    { color: #c18401; }
  .python-code .tok-string  { color: #50a14f; }
  .python-code .tok-number  { color: #986801; }
  .python-code .tok-comment { color: #9ca3af; font-style: italic; }
  .dark .python-code .tok-keyword { color: #c678dd; }
  .dark .python-code .tok-builtin { color: #61afef; }
  .dark .python-code .tok-func    { color: #e5c07b; }
  .dark .python-code .tok-string  { color: #98c379; }
  .dark .python-code .tok-number  { color: #d19a66; }
  .dark .python-code .tok-comment { color: #6b7280; }
  .python-code .tok-call { color: #4078f2; }
  .dark .python-code .tok-call { color: #61afef; }
`;

// Monokai — used where the block sits on a dark panel and must read as code first.
const MONOKAI_STYLES = `
  .python-code.monokai pre { background: #272822; color: #f8f8f2; }
  .python-code.monokai .tok-keyword,
  .dark .python-code.monokai .tok-keyword { color: #f92672; font-weight: 600; }
  .python-code.monokai .tok-builtin,
  .dark .python-code.monokai .tok-builtin { color: #66d9ef; font-style: italic; }
  .python-code.monokai .tok-func,
  .dark .python-code.monokai .tok-func    { color: #a6e22e; }
  .python-code.monokai .tok-call,
  .dark .python-code.monokai .tok-call    { color: #a6e22e; }
  .python-code.monokai .tok-string,
  .dark .python-code.monokai .tok-string  { color: #e6db74; }
  .python-code.monokai .tok-number,
  .dark .python-code.monokai .tok-number  { color: #ae81ff; }
  .python-code.monokai .tok-comment,
  .dark .python-code.monokai .tok-comment { color: #75715e; font-style: italic; }
`;

interface EditorProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  id?: string;
  ariaLabel?: string;
}

/** Editable Python field: highlighted <pre> rendered behind a transparent-text <textarea>. */
export function PythonCodeEditor({ value, onChange, rows = 8, id, ariaLabel }: EditorProps) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const preRef = useRef<HTMLPreElement>(null);

  const syncScroll = () => {
    if (preRef.current && taRef.current) {
      preRef.current.scrollTop = taRef.current.scrollTop;
      preRef.current.scrollLeft = taRef.current.scrollLeft;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    const ta = e.currentTarget;
    const { selectionStart: s, selectionEnd: end } = ta;
    const next = value.slice(0, s) + "    " + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.selectionStart = ta.selectionEnd = s + 4;
    });
  };

  // Trailing zero-width char so the highlighter's last (possibly empty) line keeps height.
  const html = highlightPython(value) + "\n";

  return (
    <div className="python-code relative rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 overflow-hidden focus-within:ring-2 focus-within:ring-[var(--color-primary)]">
      <style>{TOKEN_STYLES}</style>
      <pre
        ref={preRef}
        aria-hidden="true"
        className="text-gray-900 dark:text-gray-100 overflow-auto"
        style={{ ...TEXT_STYLE, position: "absolute", inset: 0, pointerEvents: "none" }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <textarea
        ref={taRef}
        id={id}
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onScroll={syncScroll}
        onKeyDown={handleKeyDown}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        rows={rows}
        className="relative block w-full resize-y bg-transparent outline-none overflow-auto"
        style={{ ...TEXT_STYLE, color: "transparent", caretColor: "var(--color-text-primary, #111)" }}
      />
    </div>
  );
}

interface BlockProps {
  code: string;
  className?: string;
  /** "auto" follows the app theme; "monokai" is always the dark Monokai palette. */
  theme?: "auto" | "monokai";
  /** CSS font-size for the block (e.g. "0.68rem"); defaults to the shared 12px. */
  fontSize?: string;
  /** Extra classes on the <pre> itself (padding, borders, …). */
  preClassName?: string;
}

/** Read-only highlighted Python block. */
export function PythonCodeBlock({
  code,
  className = "",
  theme = "auto",
  fontSize,
  preClassName,
}: BlockProps) {
  const monokai = theme === "monokai";
  return (
    <div className={`python-code ${monokai ? "monokai " : ""}${className}`}>
      <style>{monokai ? TOKEN_STYLES + MONOKAI_STYLES : TOKEN_STYLES}</style>
      <pre
        className={
          preClassName ??
          (monokai
            ? "overflow-x-auto border border-black"
            : "overflow-x-auto rounded bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100")
        }
        style={{
          ...TEXT_STYLE,
          // Inline padding beats a Tailwind class here, so the roomier code-panel
          // padding has to be set in the style object.
          ...(monokai ? { padding: "14px" } : null),
          ...(fontSize ? { fontSize } : null),
        }}
      >
        <code dangerouslySetInnerHTML={{ __html: highlightPython(code) }} />
      </pre>
    </div>
  );
}
